import { DOMEventDispatcher } from 'apps/webcomponents/src/utils/event.utils';
import { NodeData, TreeDataSourceOptions } from '../..';
import { Validation, ValidatorManager } from '../../validators';
import { TreeDataSource } from './dataSource';

export abstract class DraggableTreeDataSource<Options extends TreeDataSourceOptions> extends TreeDataSource<Options> {
  abstract toggleNodeOpen(node: NodeData, isOpen?: boolean): Promise<void>;

  /**
   * Override method to get notified when a node has been moved. (Mostly for API calls)
   * The moving of the node is done via pessimistic update. Throw an error if the node shouldn't be moved to the new position.
   */
  public async requestMoveNodeHandler(node: NodeData): Promise<boolean> {
    if (this.options.requestMoveNodeHandler) {
      return await this.options.requestMoveNodeHandler(node);
    }

    return true;
  }

  /**
   * Method used to move a node in the tree via drag and drop
   */
  public async dropNode(droppedNode: NodeData, containerNode: NodeData, nodeHandler: Function): Promise<Validation> {
    const { nodeIsAlreadyInContainerNode, nodeIsSameAsContainer, containerNodeIsChildToDroppedNode } =
      this.getNodeMovementPermissions(containerNode, droppedNode);

    if (nodeIsAlreadyInContainerNode || nodeIsSameAsContainer || containerNodeIsChildToDroppedNode) {
      return { nodeIsAlreadyInContainerNode, nodeIsSameAsContainer, containerNodeIsChildToDroppedNode };
    }

    const { isValid, validation } = await ValidatorManager.validate(containerNode.dropValidators || [], containerNode);

    if (!isValid) {
      return validation;
    }

    try {
      if (!containerNode.isOpen) {
        await this.toggleNodeOpen(containerNode, true);
      }

      await this.requestMoveNodeHandler(this.nodes.get(droppedNode.nodeId)!);

      nodeHandler();
      this.emitNodeChanges();

      return null;
    } catch (err) {
      console.error(err);

      return { error: err };
    }
  }

  private getNodeMovementPermissions(containerNode: NodeData, droppedNode: NodeData) {
    const nodeIsAlreadyInContainerNode = containerNode.childNodeIds?.includes(droppedNode.nodeId);
    const nodeIsSameAsContainer = droppedNode.nodeId === containerNode.nodeId;
    const containerNodeIsChildToDroppedNode = this.nodeIsChildNode(containerNode, droppedNode);

    return {
      nodeIsAlreadyInContainerNode,
      nodeIsSameAsContainer,
      containerNodeIsChildToDroppedNode,
    };
  }

  public updateNodeWithNewPosition(droppedNode: NodeData, containerNodeId: string | number) {
    const parentNode = this.nodes.get(droppedNode.parentNodeId!);
    const containerNode = this.nodes.get(containerNodeId);

    if (parentNode && containerNode) {
      parentNode.childNodeIds = parentNode?.childNodeIds?.filter(childNodeId => childNodeId !== droppedNode.nodeId);
      this.nodes.set(parentNode.nodeId, parentNode);
      this.nodes.set(containerNode.nodeId, {
        ...containerNode,
        childNodeIds: [...(containerNode.childNodeIds || []), droppedNode.nodeId],
      });
      this.nodes.set(droppedNode.nodeId, { ...droppedNode, parentNodeId: containerNode.nodeId });
    }
  }
}
